/**
 * FOREGROUND NOTIFICATION HANDLER
 * 
 * Hook to listen for and display notifications when app is in focus
 * Shows a toast notification that can be clicked to navigate
 * 
 * Location: src/hooks/useForegroundNotifications.ts
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onForegroundMessage } from '../firebase/notifications';
import type { MessagePayload } from 'firebase/messaging';

interface NotificationToast {
  id: string;
  title: string;
  body: string;
  type?: string;
  data?: Record<string, string>;
  timestamp: number;
}

/**
 * Hook to handle foreground notifications
 * Returns current toasts and functions to manage them
 */
export const useForegroundNotifications = () => {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<NotificationToast[]>([]);

  // Handle incoming foreground message
  const handleMessage = useCallback((payload: MessagePayload) => {
    const notification = payload.notification;
    const data = payload.data as Record<string, string> | undefined;

    if (notification) {
      const toast: NotificationToast = {
        id: `${Date.now()}-${Math.random()}`,
        title: notification.title || 'New Notification',
        body: notification.body || '',
        type: data?.type,
        data,
        timestamp: Date.now()
      };

      setToasts(prev => [...prev, toast]);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        dismissToast(toast.id);
      }, 5000);

      // Play notification sound (optional)
      playNotificationSound();
    }
  }, []);

  // Subscribe to foreground messages
  useEffect(() => {
    const unsubscribe = onForegroundMessage(handleMessage);
    return () => unsubscribe();
  }, [handleMessage]);

  // Dismiss a toast
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Handle toast click - navigate to relevant page
  const handleToastClick = useCallback((toast: NotificationToast) => {
    dismissToast(toast.id);

    const data = toast.data || {};
    
    switch (toast.type) {
      case 'message':
        navigate(`/messages/${data.conversationId || ''}`);
        break;
      case 'destash_inquiry':
        navigate('/collection?tab=destash');
        break;
      case 'wishlist_available':
        navigate(data.url || '/my-wants');
        break;
      case 'new_follower':
        navigate('/profile');
        break;
      default:
        if (data.url) {
          navigate(data.url);
        }
    }
  }, [navigate, dismissToast]);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return {
    toasts,
    dismissToast,
    handleToastClick,
    clearAllToasts
  };
};

/**
 * Play a subtle notification sound
 */
const playNotificationSound = () => {
  try {
    // Create a simple beep using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    // Audio not supported or blocked, fail silently
  }
};

export default useForegroundNotifications;
